import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Users, Globe, ChevronRight, Stamp, X } from 'lucide-react';
const experiences = {
  bali: {
    title: 'The Bali Experience',
    dates: 'Jan 25 – Feb 2, 2027',
    persona: 'The mindful traveler seeking peace, luxury villas, and deep connection — celebrating the birthday in serenity on Jan 27.',
    color: 'bg-emerald'
  },
  brazil: {
    title: 'The Brazil Experience',
    dates: 'Feb 4 – Feb 10, 2027 • Rio Carnival',
    persona: 'The energetic soul ready to dance through Carnival — parades peak Feb 7–9.',
    color: 'bg-fuchsia'
  },
  botswana: {
    title: 'The Botswana Experience',
    dates: 'Feb 11 – Feb 15, 2027',
    persona: 'The adventurer concluding the journey with raw nature, luxury safaris, and legacy.',
    color: 'bg-sunset'
  },
  all: {
    title: 'The Global Tour (All 3)',
    dates: 'Jan 25 – Feb 15, 2027',
    persona: 'The ultimate global citizen ready for the trip of a lifetime.',
    color: 'bg-gold'
  }
};
type ExpKey = keyof typeof experiences;
export function ExperienceSelector() {
  const [selected, setSelected] = useState<ExpKey>('bali');
  const [isPassportOpen, setIsPassportOpen] = useState(false);
  // Close passport when switching tabs
  useEffect(() => {
    setIsPassportOpen(false);
  }, [selected]);
  return <section className="py-32 bg-rich-black relative border-t border-white/5" data-id="element-90">
      <div className="container mx-auto px-6" data-id="element-91">
        <div className="text-center mb-16" data-id="element-92">
          <h2 className="font-display text-4xl md:text-5xl font-bold mb-6" data-id="element-93">
            Pick Your <span className="text-gradient-gold" data-id="element-94">Experience</span>
          </h2>
          <p className="font-sans text-lg text-gray-400 max-w-2xl mx-auto" data-id="element-95">
            Whether you join for one destination or the entire world tour,
            select your vibe below to get more details and secure your spot.
          </p>
        </div>

        <div className="max-w-5xl mx-auto" data-id="element-96">
          {/* Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12" data-id="element-97">
            {(Object.keys(experiences) as ExpKey[]).map(key => <button key={key} onClick={() => setSelected(key)} className={`px-6 py-3 rounded-full font-sans font-medium transition-all duration-300 ${selected === key ? `${experiences[key].color} text-white shadow-lg scale-105` : 'glass-panel text-gray-400 hover:text-white hover:border-white/30'}`} data-id="element-98">
                {key === 'all' ? "I'm doing all 3" : `Join in ${key.charAt(0).toUpperCase() + key.slice(1)}`}
              </button>)}
          </div>

          {/* Content Area */}
          <div className="glass-panel rounded-3xl p-8 md:p-12 relative overflow-hidden" data-id="element-99">
            <AnimatePresence mode="wait" data-id="element-100">
              <motion.div key={selected} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -20
            }} transition={{
              duration: 0.3
            }} className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-101">
                {/* Details */}
                <div data-id="element-102">
                  <h3 className="font-display text-3xl font-bold text-white mb-8" data-id="element-103">
                    {experiences[selected].title}
                  </h3>

                  <div className="space-y-6 mb-8" data-id="element-104">
                    <div className="flex items-start gap-4" data-id="element-105">
                      <div className={`p-3 rounded-full bg-white/5 text-white`} data-id="element-106">
                        <Calendar size={24} data-id="element-107" />
                      </div>
                      <div data-id="element-108">
                        <p className="font-sans text-sm text-gray-400 uppercase tracking-wider" data-id="element-109">
                          Tentative Dates
                        </p>
                        <p className="font-sans text-lg font-medium text-white" data-id="element-110">
                          {experiences[selected].dates}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4" data-id="element-111">
                      <div className={`p-3 rounded-full bg-white/5 text-white`} data-id="element-112">
                        <Users size={24} data-id="element-113" />
                      </div>
                      <div data-id="element-114">
                        <p className="font-sans text-sm text-gray-400 uppercase tracking-wider" data-id="element-115">
                          Ideal For
                        </p>
                        <p className="font-sans text-lg font-medium text-white" data-id="element-116">
                          {experiences[selected].persona}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Passport Form UI */}
                <div className="relative w-full max-w-sm mx-auto aspect-[3/4] [perspective:2000px]" data-id="element-117">
                  <motion.div className="w-full h-full relative [transform-style:preserve-3d] cursor-pointer" animate={{
                  rotateY: isPassportOpen ? -180 : 0
                }} transition={{
                  duration: 0.8,
                  type: 'spring',
                  stiffness: 60,
                  damping: 15
                }} data-id="element-118">
                    {/* Front Cover */}
                    <div className={`absolute inset-0 [backface-visibility:hidden] rounded-2xl shadow-2xl flex flex-col items-center justify-center p-8 border border-gold/30 ${experiences[selected].color} bg-blend-overlay bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-black/10 to-black/50`} onClick={() => setIsPassportOpen(true)} data-id="element-119">
                      <Globe className="w-16 h-16 text-gold mb-6 opacity-90" strokeWidth={1.5} data-id="element-120" />
                      <h4 className="font-display text-3xl text-gold tracking-[0.3em] mb-2 text-center" data-id="element-121">
                        PASSPORT
                      </h4>
                      <p className="font-sans text-[10px] text-gold/80 tracking-widest uppercase mb-12 text-center" data-id="element-122">
                        Express Interest
                      </p>

                      <div className="absolute bottom-8 flex flex-col items-center gap-2 text-gold/60 animate-pulse" data-id="element-123">
                        <span className="font-sans text-[10px] uppercase tracking-widest" data-id="element-124">
                          Tap to Open
                        </span>
                        <ChevronRight className="w-4 h-4 rotate-90" data-id="element-125" />
                      </div>
                    </div>

                    {/* Inside Page (Form) */}
                    <div className="absolute inset-0 [backface-visibility:hidden] rounded-2xl shadow-2xl bg-[#FDFBF7] p-8 overflow-y-auto border-2 border-gold/20" style={{
                    transform: 'rotateY(180deg)'
                  }} data-id="element-126">
                      <button onClick={e => {
                      e.stopPropagation();
                      setIsPassportOpen(false);
                    }} className="absolute top-4 right-4 text-gray-400 hover:text-gray-800 transition-colors" data-id="element-127">
                        <X size={20} data-id="element-128" />
                      </button>

                      <div className="border-b-2 border-gold/30 pb-4 mb-6 mt-2" data-id="element-129">
                        <h4 className="font-display text-xl text-gray-800 tracking-widest uppercase" data-id="element-130">
                          Personal Details
                        </h4>
                        <p className="font-sans text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-131">
                          Official Entry Request
                        </p>
                      </div>

                      <form className="space-y-5" onSubmit={e => e.preventDefault()} data-id="element-132">
                        <div data-id="element-133">
                          <label className="block font-sans text-[10px] text-gray-500 uppercase tracking-wider mb-1" data-id="element-134">
                            Full Name
                          </label>
                          <input type="text" className="w-full bg-transparent border-b border-gray-300 px-0 py-2 text-gray-800 font-sans focus:outline-none focus:border-gold transition-colors" data-id="element-135" />
                        </div>
                        <div data-id="element-136">
                          <label className="block font-sans text-[10px] text-gray-500 uppercase tracking-wider mb-1" data-id="element-137">
                            Email Address
                          </label>
                          <input type="email" className="w-full bg-transparent border-b border-gray-300 px-0 py-2 text-gray-800 font-sans focus:outline-none focus:border-gold transition-colors" data-id="element-138" />
                        </div>
                        <div data-id="element-139">
                          <label className="block font-sans text-[10px] text-gray-500 uppercase tracking-wider mb-1" data-id="element-140">
                            Special Requests
                          </label>
                          <textarea rows={2} className="w-full bg-transparent border-b border-gray-300 px-0 py-2 text-gray-800 font-sans focus:outline-none focus:border-gold transition-colors resize-none" data-id="element-141"></textarea>
                        </div>
                        <button className={`w-full py-4 mt-2 rounded-xl font-sans font-bold text-white flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] shadow-lg ${experiences[selected].color}`} data-id="element-142">
                          <Stamp size={20} data-id="element-143" /> Stamp My Passport
                        </button>
                      </form>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>;
}